import { createContext, useContext, useState, useEffect, useRef, useCallback, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/components/ui/sonner";
import { playRingtone, playDialTone, stopRingtone, startVibration, stopVibration } from "@/lib/ringtone";
import { lazy, Suspense } from "react";
import type { CallType } from "@/services/calls/types";
import { logCallHistory, notifyMissedCall } from "@/services/calls/callSessionManager";
import { cancelPendingDispatch } from "@/lib/cancelPendingDispatch";
const CallOverlay = lazy(() => import("@/components/chat/CallOverlay"));
const IncomingCallSheet = lazy(() => import("@/components/chat/IncomingCallSheet"));

interface ActiveCall {
  conversationId: string;
  remoteUserId: string;
  remoteName: string;
  remoteAvatar: string | null;
  callType: CallType;
  isIncoming: boolean;
  localStream?: MediaStream;
  isAiCall?: boolean;
}

interface IncomingCallData {
  conversationId: string;
  callType: CallType;
  from: string;
  callerName: string;
  callerAvatar: string | null;
}

interface CallContextType {
  startCall: (conversationId: string, remoteUserId: string, remoteName: string, remoteAvatar: string | null, type: CallType) => Promise<void>;
  stopInviteSignaling: () => void;
  cancelOutgoingCall: () => void;
  restartInviteSignaling: () => Promise<void>;
  activeCall: ActiveCall | null;
  incomingCall: IncomingCallData | null;
  dnd: boolean;
  setDnd: (v: boolean) => void;
  minimized: boolean;
  setMinimized: (v: boolean) => void;
}

// Pin CallContext across HMR reloads so consumers and provider always share
// the same context object. Without this, editing CallContext.tsx in dev causes
// `useContext(CallContext)` to return null for in-flight renders, crashing the
// whole tree (blank page + spinner).
const __CALL_CTX_KEY = "__whisprai_call_context__";
const CallContext: React.Context<CallContextType | null> =
  (globalThis as any)[__CALL_CTX_KEY] ??
  ((globalThis as any)[__CALL_CTX_KEY] = createContext<CallContextType | null>(null));

let __useCallWarned = false;
export function useCall() {
  const ctx = useContext(CallContext);
  if (!ctx) {
    if (import.meta.env.DEV) {
      if (!__useCallWarned) {
        __useCallWarned = true;
        // eslint-disable-next-line no-console
        console.warn("[useCall] context missing — likely HMR transient. Returning empty stub.");
      }
      return {} as CallContextType;
    }
    throw new Error("useCall must be used within CallProvider");
  }
  return ctx;
}

export function CallProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [activeCall, setActiveCallRaw] = useState<ActiveCall | null>(null);
  const [minimized, setMinimized] = useState(false);

  const setActiveCall = useCallback((call: ActiveCall | null) => {
    setMinimized(false);
    setActiveCallRaw(call);
  }, []);
  const [incomingCall, setIncomingCall] = useState<IncomingCallData | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const ringingTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const activeCallRef = useRef<ActiveCall | null>(null);
  const inviteIntervalRef = useRef<ReturnType<typeof setInterval>>();
  const inviteChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const incomingFromRef = useRef<string | null>(null);
  const [channelResetKey, setChannelResetKey] = useState(0);
  const retryCountRef = useRef(0);
  const humanPickedUpRef = useRef(false);
  const declinedCallersRef = useRef(new Map<string, number>());
  const [dnd, setDndState] = useState(false);
  const dndRef = useRef(false);
  const chatbotEnabledRef = useRef(false);
  const voicemailEnabledRef = useRef(true);

  const setDnd = useCallback((v: boolean) => {
    setDndState(v);
    dndRef.current = v;
  }, []);

  // Fetch DND + chatbot_enabled on mount
  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("do_not_disturb, chatbot_enabled, voicemail_enabled")
      .eq("user_id", user.id)
      .single()
      .then(({ data }) => {
        const v = (data as any)?.do_not_disturb ?? false;
        setDndState(v);
        dndRef.current = v;
        chatbotEnabledRef.current = (data as any)?.chatbot_enabled ?? false;
        voicemailEnabledRef.current = (data as any)?.voicemail_enabled ?? true;
      });
  }, [user]);

  const stopInviteSignaling = useCallback(() => {
    clearInterval(inviteIntervalRef.current);
    if (inviteChannelRef.current) {
      const ch = inviteChannelRef.current;
      inviteChannelRef.current = null;
      // Silently remove channel — no call-cancel broadcast.
      // call-cancel is only sent explicitly when the caller hangs up before answer.
      setTimeout(() => supabase.removeChannel(ch), 200);
    }
  }, []);

  /** Explicitly cancel an outgoing call (caller hangs up before answer) */
  const cancelOutgoingCall = useCallback(() => {
    clearInterval(inviteIntervalRef.current);
    if (inviteChannelRef.current) {
      const ch = inviteChannelRef.current;
      inviteChannelRef.current = null;
      ch.send({
        type: "broadcast",
        event: "call-signal",
        payload: { type: "call-cancel", from: user?.id },
      }).then(() => {
        setTimeout(() => supabase.removeChannel(ch), 200);
      }).catch(() => {
        supabase.removeChannel(ch);
      });
    }
  }, [user]);

  // Keep ref in sync so the listener doesn't need activeCall as a dep
  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  // Global incoming-call listener scoped to user ID
  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    let ch: ReturnType<typeof supabase.channel> | null = null;

    const setup = () => {
      if (cancelled) return;

      const channelName = `call-incoming-${user.id}`;
      ch = supabase.channel(channelName)
        .on("broadcast", { event: "call-signal" }, async ({ payload }) => {
          if (payload.from === user.id) return;
          console.log("[Call][Incoming] Signal received:", payload.type, "from:", payload.from);
          if (payload.type === "call-invite") {
            // Don't show if already in a call
            if (activeCallRef.current) {
              console.log("[Call][Incoming] Already in a call, ignoring invite");
              return;
            }
            // Skip if this caller was recently declined (60s cooldown)
            const declinedAt = declinedCallersRef.current.get(payload.from);
            if (declinedAt && Date.now() - declinedAt < 60000) return;

            // Skip duplicate invites from same caller (repeating interval)
            if (incomingFromRef.current === payload.from) return;

            // Check per-chat call preferences
            if (payload.conversationId) {
              const { data: myMembership } = await supabase
                .from("conversation_members")
                .select("allow_voice_calls, allow_video_calls")
                .eq("conversation_id", payload.conversationId)
                .eq("user_id", user.id)
                .maybeSingle();
              if (myMembership) {
                const isVideo = payload.callType === "video";
                if (isVideo && (myMembership as any).allow_video_calls === false) {
                  console.log("[Call][Incoming] Video calls disabled for this chat");
                  return;
                }
                if (!isVideo && (myMembership as any).allow_voice_calls === false) {
                  console.log("[Call][Incoming] Voice calls disabled for this chat");
                  return;
                }
              }
            }

            // DND: auto-decline
            if (dndRef.current) {
              console.log("[Call][Incoming] DND active, auto-declining");
              // Send cancel to caller
              const callerCh = supabase.channel(`call-incoming-${payload.from}`);
              const subTimeout = setTimeout(() => supabase.removeChannel(callerCh), 5000);
              callerCh.subscribe((st) => {
                if (st === "SUBSCRIBED") {
                  clearTimeout(subTimeout);
                  callerCh.send({
                    type: "broadcast",
                    event: "call-signal",
                    payload: { type: "call-cancel", from: user.id },
                  });
                  setTimeout(() => supabase.removeChannel(callerCh), 500);
                }
                if (st === "TIMED_OUT" || st === "CHANNEL_ERROR") {
                  clearTimeout(subTimeout);
                  supabase.removeChannel(callerCh);
                }
              });
              // Log call history + notification
              Promise.all([
                logCallHistory({
                  callerId: payload.from,
                  receiverId: user.id,
                  conversationId: payload.conversationId,
                  callType: payload.callType ?? "voice",
                  status: "declined",
                }),
                notifyMissedCall({
                  userId: user.id,
                  callerName: payload.callerName ?? "Unknown",
                  callType: payload.callType ?? "voice",
                  title: "Missed Call (DND)",
                }),
              ]);
              return;
            }

            incomingFromRef.current = payload.from;
            console.log("[Call][Incoming] Showing incoming call from:", payload.callerName);

            setIncomingCall({
              conversationId: payload.conversationId,
              callType: payload.callType ?? "voice",
              from: payload.from,
              callerName: payload.callerName ?? "Unknown",
              callerAvatar: payload.callerAvatar ?? null,
            });

            // Play ringtone and vibrate
            playRingtone();
            startVibration();

            // Auto-decline timeout: 10s if AI bot is on, 20s otherwise
            const timeoutMs = chatbotEnabledRef.current ? 10000 : 20000;
            ringingTimeoutRef.current = setTimeout(async () => {
              stopRingtone();
              stopVibration();
              setIncomingCall(null);
              incomingFromRef.current = null;

              if (chatbotEnabledRef.current && user) {
                // === AI AUTO-ANSWER: Bot picks up on behalf of user ===
                console.log("[Call][Incoming] AI bot auto-answering call from", payload.callerName);

                // Send signal to caller that AI answered
                const callerCh = supabase.channel(`call-incoming-${payload.from}`);
                const subTimeout = setTimeout(() => supabase.removeChannel(callerCh), 8000);
                callerCh.subscribe((st) => {
                  if (st === "SUBSCRIBED") {
                    clearTimeout(subTimeout);
                    // Invoke AI greeting server-side, then broadcast result to caller
                    supabase.functions.invoke("ai-voice-call", {
                      body: {
                        greeting: true,
                        conversation_id: payload.conversationId,
                        sender_id: payload.from,
                        recipient_id: user.id,
                      },
                    }).then(({ data: greetingData }) => {
                      callerCh.send({
                        type: "broadcast",
                        event: "call-signal",
                        payload: {
                          type: "call-ai-answer",
                          from: user.id,
                          conversationId: payload.conversationId,
                          greeting_audio: greetingData?.audio_base64 ?? null,
                          greeting_text: greetingData?.aiResponse ?? null,
                        },
                      });
                      setTimeout(() => supabase.removeChannel(callerCh), 1000);
                    }).catch((err) => {
                      console.error("[Call] AI greeting failed:", err);
                      // Still tell caller AI answered, just without greeting audio
                      callerCh.send({
                        type: "broadcast",
                        event: "call-signal",
                        payload: { type: "call-ai-answer", from: user.id, conversationId: payload.conversationId },
                      });
                      setTimeout(() => supabase.removeChannel(callerCh), 1000);
                    });
                  }
                  if (st === "TIMED_OUT" || st === "CHANNEL_ERROR") {
                    clearTimeout(subTimeout);
                    supabase.removeChannel(callerCh);
                  }
                });

                // Log as AI-answered
                await Promise.all([
                  notifyMissedCall({
                    userId: user.id,
                    callerName: payload.callerName ?? "Unknown",
                    callType: payload.callType ?? "voice",
                    title: "AI Answered Call",
                  }),
                  logCallHistory({
                    callerId: payload.from,
                    receiverId: user.id,
                    conversationId: payload.conversationId,
                    callType: payload.callType ?? "voice",
                    status: "ai_answered",
                  }),
                ]);
              } else if (voicemailEnabledRef.current) {
                // === VOICEMAIL FALLBACK: Bot is off, voicemail is on ===
                toast.info("Missed call");

                // Send voicemail signal to caller
                const callerCh = supabase.channel(`call-incoming-${payload.from}`);
                const subTimeout = setTimeout(() => supabase.removeChannel(callerCh), 5000);
                callerCh.subscribe((st) => {
                  if (st === "SUBSCRIBED") {
                    clearTimeout(subTimeout);
                    callerCh.send({
                      type: "broadcast",
                      event: "call-signal",
                      payload: { type: "call-voicemail", from: user?.id },
                    });
                    setTimeout(() => supabase.removeChannel(callerCh), 500);
                  }
                  if (st === "TIMED_OUT" || st === "CHANNEL_ERROR") {
                    clearTimeout(subTimeout);
                    supabase.removeChannel(callerCh);
                  }
                });

                // Log missed call
                if (user) {
                  await Promise.all([
                    notifyMissedCall({
                      userId: user.id,
                      callerName: payload.callerName ?? "Unknown",
                      callType: payload.callType ?? "voice",
                    }),
                    logCallHistory({
                      callerId: payload.from,
                      receiverId: user.id,
                      conversationId: payload.conversationId,
                      callType: payload.callType ?? "voice",
                      status: "missed",
                    }),
                  ]);
                }
              } else {
                // === PLAIN MISSED CALL: Both bot and voicemail are off ===
                toast.info("Missed call");

                if (user) {
                  await Promise.all([
                    notifyMissedCall({
                      userId: user.id,
                      callerName: payload.callerName ?? "Unknown",
                      callType: payload.callType ?? "voice",
                      title: "Missed Call",
                    }),
                    logCallHistory({
                      callerId: payload.from,
                      receiverId: user.id,
                      conversationId: payload.conversationId,
                      callType: payload.callType ?? "voice",
                      status: "missed",
                    }),
                  ]);
                }
              }
            }, timeoutMs);
          }

          if (payload.type === "call-cancel") {
            console.log("[Call][Incoming] Caller cancelled");
            stopRingtone();
            stopVibration();
            clearTimeout(ringingTimeoutRef.current);
            setIncomingCall(null);
            incomingFromRef.current = null;
          }
        })
        .subscribe((status) => {
          console.log("[Call] Incoming-call listener status:", status);
          if (status === "SUBSCRIBED") {
            retryCountRef.current = 0;
          }
          if (status === "TIMED_OUT" || status === "CHANNEL_ERROR") {
            if (retryCountRef.current < 5) {
              retryCountRef.current++;
              console.log(`[Call] Subscription failed (${status}), retry ${retryCountRef.current}/5 in 3s...`);
              // Do NOT call removeChannel here -- let the cleanup handle it
              setTimeout(() => {
                if (cancelled) return;
                setChannelResetKey(k => k + 1);
              }, 3000);
            } else {
              console.error("[Call] Max retry attempts reached for incoming-call listener.");
            }
          }
        });

      channelRef.current = ch;
    };

    // On recreation (channelResetKey > 0), delay to let the server
    // process the leave from the previous channel before joining again
    if (channelResetKey > 0) {
      const delayTimer = setTimeout(setup, 1000);
      return () => {
        cancelled = true;
        clearTimeout(delayTimer);
        if (ch) {
          supabase.removeChannel(ch);
          channelRef.current = null;
        }
        clearTimeout(ringingTimeoutRef.current);
      };
    } else {
      setup();
      return () => {
        cancelled = true;
        if (ch) {
          supabase.removeChannel(ch);
          channelRef.current = null;
        }
        clearTimeout(ringingTimeoutRef.current);
      };
    }
  }, [user, channelResetKey]);

  const startCall = useCallback(async (
    conversationId: string,
    remoteUserId: string,
    remoteName: string,
    remoteAvatar: string | null,
    type: CallType
  ) => {
    if (!user) return;

    // Cancel any pending missed-call email — user is calling them back
    cancelPendingDispatch({ userId: user.id, conversationId, eventType: "missed_call" });

    try {
      // Check if the remote user is an AI profile (pure AI or real user with chatbot)
      const [{ data: remoteProfile }, { data: aiGenCheck }] = await Promise.all([
        supabase
          .from("profiles")
          .select("chatbot_enabled")
          .eq("user_id", remoteUserId)
          .single(),
        supabase
          .from("ai_generated_profiles")
          .select("id")
          .eq("generated_profile_user_id", remoteUserId)
          .limit(1)
          .maybeSingle(),
      ]);

      const hasChatbot = remoteProfile?.chatbot_enabled === true;
      const isPureAiProfile = !!aiGenCheck; // AI-generated profile — never a real human
      const isAiCall = hasChatbot;

      if (isAiCall && isPureAiProfile) {
        // Pure AI profile: direct AI voice path ONLY — no parallel human invite signaling
        humanPickedUpRef.current = false;

        setActiveCall({
          conversationId,
          remoteUserId,
          remoteName,
          remoteAvatar,
          callType: "voice",
          isIncoming: false,
          isAiCall: true,
        });
        // No push for pure AI calls
        return;
      }

      if (isAiCall && !isPureAiProfile) {
        // Real user with chatbot enabled: start AI call path BUT also signal human in parallel
        // so they can pick up if they're online
        humanPickedUpRef.current = false;

        // Start human invite signaling in parallel
        const { data: callerProfile } = await supabase
          .from("profiles")
          .select("display_name, avatar_url")
          .eq("user_id", user.id)
          .single();

        const callerDisplayName = callerProfile?.display_name ?? "Unknown";
        const callerAvatarUrl = callerProfile?.avatar_url ?? null;

        stopInviteSignaling();
        const remoteChannel = supabase.channel(`call-incoming-${remoteUserId}`, {
          config: { broadcast: { ack: true } },
        });
        inviteChannelRef.current = remoteChannel;

        remoteChannel
        .on("broadcast", { event: "call-signal" }, ({ payload }) => {
          if (payload?.type === "call-cancel") {
            console.log("[Call] Receiver declined (hybrid path)");
            clearInterval(inviteIntervalRef.current);
            stopRingtone();
            stopDialTone();
            if (inviteChannelRef.current) {
              const ch = inviteChannelRef.current;
              inviteChannelRef.current = null;
              setTimeout(() => supabase.removeChannel(ch), 200);
            }
            setActiveCall(null);
            toast.info("Call declined");
          }
        })
        .subscribe((status) => {
          if (status === "SUBSCRIBED") {
            const sendInvite = async () => {
              await remoteChannel.send({
                type: "broadcast",
                event: "call-signal",
                payload: {
                  type: "call-invite",
                  conversationId,
                  callType: "voice",
                  from: user.id,
                  callerName: callerDisplayName,
                  callerAvatar: callerAvatarUrl,
                },
              });
            };
            sendInvite();
            inviteIntervalRef.current = setInterval(sendInvite, 2000);
          }
        });

        // Fire-and-forget push notification for hybrid path
        supabase.functions.invoke("send-call-push", {
          body: {
            caller_id: user.id,
            receiver_id: remoteUserId,
            conversation_id: conversationId,
            call_type: "voice",
            caller_name: callerDisplayName,
            caller_avatar: callerAvatarUrl,
            caller_type: "human",
          },
        }).catch((e) => console.warn("[Call] Push notification failed (non-critical):", e));

        setActiveCall({
          conversationId,
          remoteUserId,
          remoteName,
          remoteAvatar,
          callType: "voice",
          isIncoming: false,
          isAiCall: true,
        });
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: type === "video",
      });

      // Fetch caller profile before subscribing to avoid delay
      const { data: callerProfile } = await supabase
        .from("profiles")
        .select("display_name, avatar_url")
        .eq("user_id", user.id)
        .single();

      const callerDisplayName = callerProfile?.display_name ?? "Unknown";
      const callerAvatarUrl = callerProfile?.avatar_url ?? null;

      // Signal the remote user via their incoming channel — repeat every 2s
      stopInviteSignaling(); // clean up any previous invite
      const remoteChannel = supabase.channel(`call-incoming-${remoteUserId}`, {
        config: { broadcast: { ack: true } },
      });
      inviteChannelRef.current = remoteChannel;

      // Safeguard: if subscription doesn't succeed in 10s, warn the caller
      const inviteSubTimeout = setTimeout(() => {
        if (inviteChannelRef.current === remoteChannel) {
          console.error("[Call] Invite channel subscription timed out after 10s");
          toast("Connection issue — call may not be reaching the other user.");
        }
      }, 10000);

      remoteChannel
      .on("broadcast", { event: "call-signal" }, ({ payload }) => {
        if (payload?.type === "call-cancel") {
          console.log("[Call] Receiver declined call");
          clearInterval(inviteIntervalRef.current);
          stopRingtone();
          stopDialTone();
          if (inviteChannelRef.current) {
            const ch = inviteChannelRef.current;
            inviteChannelRef.current = null;
            setTimeout(() => supabase.removeChannel(ch), 200);
          }
          setActiveCall(null);
          toast.info("Call declined");
        }
      })
      .subscribe((status) => {
        console.log("[Call] Invite channel status:", status);
        if (status === "SUBSCRIBED") {
          clearTimeout(inviteSubTimeout);
          const sendInvite = async () => {
            const res = await remoteChannel.send({
              type: "broadcast",
              event: "call-signal",
              payload: {
                type: "call-invite",
                conversationId,
                callType: type,
                from: user.id,
                callerName: callerDisplayName,
                callerAvatar: callerAvatarUrl,
              },
            });
            console.log("[Call] Invite sent, ack:", res);
          };
          sendInvite();
          inviteIntervalRef.current = setInterval(sendInvite, 2000);
        }
        if (status === "TIMED_OUT" || status === "CHANNEL_ERROR") {
          clearTimeout(inviteSubTimeout);
          console.error("[Call] Invite channel subscription failed:", status);
          toast("Could not reach the other user. Please try again.");
        }
      });

      // Play dial tone for caller
      playDialTone();

      // Fire-and-forget push notification to wake up recipient's device
      supabase.functions.invoke("send-call-push", {
        body: {
          caller_id: user.id,
          receiver_id: remoteUserId,
          conversation_id: conversationId,
          call_type: type,
          caller_name: callerDisplayName,
          caller_avatar: callerAvatarUrl,
          caller_type: "human",
        },
      }).catch((e) => console.warn("[Call] Push notification failed (non-critical):", e));

      setActiveCall({
        conversationId,
        remoteUserId,
        remoteName,
        remoteAvatar,
        callType: type,
        isIncoming: false,
        localStream: stream,
      });
    } catch {
      toast("Could not access camera/microphone. Please check permissions.");
    }
  }, [user, stopInviteSignaling]);

  const acceptCall = useCallback(async () => {
    if (!incomingCall || !user) return;

    stopRingtone();
    stopVibration();
    clearTimeout(ringingTimeoutRef.current);

    try {
      // Check if the caller is an AI profile before acquiring media
      const { data: callerProfile } = await supabase
        .from("profiles")
        .select("chatbot_enabled")
        .eq("user_id", incomingCall.from)
        .single();

      const isAiCaller = callerProfile?.chatbot_enabled === true;

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: isAiCaller ? false : incomingCall.callType === "video",
      });

      setActiveCall({
        conversationId: incomingCall.conversationId,
        remoteUserId: incomingCall.from,
        remoteName: incomingCall.callerName,
        remoteAvatar: incomingCall.callerAvatar,
        callType: isAiCaller ? "voice" : incomingCall.callType,
        isIncoming: true,
        isAiCall: isAiCaller,
        localStream: stream,
      });
      setIncomingCall(null);
      incomingFromRef.current = null;
    } catch {
      toast("Could not access camera/microphone. Please check permissions.");
      setIncomingCall(null);
      incomingFromRef.current = null;
    }
  }, [incomingCall, user]);

  const declineCall = useCallback(() => {
    if (!incomingCall || !user) return;

    stopRingtone();
    stopVibration();
    clearTimeout(ringingTimeoutRef.current);

    // Remember this caller was declined (prevents re-ring from remaining broadcasts)
    declinedCallersRef.current.set(incomingCall.from, Date.now());

    // Broadcast call-cancel on our OWN incoming channel (call-incoming-{user.id}).
    // The caller is already subscribed to this channel (that's how they send invites),
    // so they will receive this signal immediately.
    if (channelRef.current) {
      channelRef.current.send({
        type: "broadcast",
        event: "call-signal",
        payload: { type: "call-cancel", from: user.id },
      });
    }

    setIncomingCall(null);
    incomingFromRef.current = null;
  }, [incomingCall, user]);

  const restartInviteSignaling = useCallback(async () => {
    if (!user || !activeCallRef.current) return;
    const currentCall = activeCallRef.current;

    // Fetch caller profile
    const { data: callerProfile } = await supabase
      .from("profiles")
      .select("display_name, avatar_url")
      .eq("user_id", user.id)
      .single();

    const callerDisplayName = callerProfile?.display_name ?? "Unknown";
    const callerAvatarUrl = callerProfile?.avatar_url ?? null;

    stopInviteSignaling();
    const remoteChannel = supabase.channel(`call-incoming-${currentCall.remoteUserId}`, {
      config: { broadcast: { ack: true } },
    });
    inviteChannelRef.current = remoteChannel;

    remoteChannel
      .on("broadcast", { event: "call-signal" }, ({ payload }) => {
        if (payload?.type === "call-cancel") {
          console.log("[Call][Retry] Receiver declined call");
          clearInterval(inviteIntervalRef.current);
          stopRingtone();
          stopDialTone();
          if (inviteChannelRef.current) {
            const ch = inviteChannelRef.current;
            inviteChannelRef.current = null;
            setTimeout(() => supabase.removeChannel(ch), 200);
          }
          setActiveCall(null);
          toast.info("Call declined");
        }
      })
      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          const sendInvite = async () => {
            await remoteChannel.send({
              type: "broadcast",
              event: "call-signal",
              payload: {
                type: "call-invite",
                conversationId: currentCall.conversationId,
                callType: currentCall.callType,
                from: user.id,
                callerName: callerDisplayName,
                callerAvatar: callerAvatarUrl,
              },
            });
          };
          sendInvite();
          inviteIntervalRef.current = setInterval(sendInvite, 2000);
        }
      });

    // Re-send push notification
    supabase.functions.invoke("send-call-push", {
      body: {
        caller_id: user.id,
        receiver_id: currentCall.remoteUserId,
        conversation_id: currentCall.conversationId,
        call_type: currentCall.callType,
        caller_name: callerDisplayName,
        caller_avatar: callerAvatarUrl,
        caller_type: "human",
      },
    }).catch((e) => console.warn("[Call][Retry] Push notification failed:", e));

    // Restart dial tone
    playDialTone();
  }, [user, stopInviteSignaling, setActiveCall]);

  const handleCallEnd = useCallback(async (durationSeconds: number) => {
    stopRingtone();
    stopDialTone();

    const currentCall = activeCall;

    // If this was an AI call and the human picked up, transition to regular WebRTC call
    if (currentCall?.isAiCall && durationSeconds >= 0) {
      // Check if human actually answered by looking for an active incoming call channel
      // The human pickup is signaled by the CallOverlay detecting call-ready/call-answer
      // We detect this by checking if the invite signaling is still active (human answered within timeout)
      const wasHumanPickup = humanPickedUpRef.current;
      humanPickedUpRef.current = false;

      if (wasHumanPickup && user) {
        // Transition: restart as regular WebRTC call
        console.log("[Call] Transitioning from AI to regular WebRTC call");
        setActiveCall(null);

        // Small delay to let cleanup finish, then start regular call
        setTimeout(async () => {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            setActiveCall({
              conversationId: currentCall.conversationId,
              remoteUserId: currentCall.remoteUserId,
              remoteName: currentCall.remoteName,
              remoteAvatar: currentCall.remoteAvatar,
              callType: "voice",
              isIncoming: false,
              localStream: stream,
              isAiCall: false,
            });

            // Re-send invite signaling
            const { data: callerProfile } = await supabase
              .from("profiles")
              .select("display_name, avatar_url")
              .eq("user_id", user.id)
              .single();

            const callerDisplayName = callerProfile?.display_name ?? "Unknown";
            const callerAvatarUrl = callerProfile?.avatar_url ?? null;

            stopInviteSignaling();
            const remoteChannel = supabase.channel(`call-incoming-${currentCall.remoteUserId}`, {
              config: { broadcast: { ack: true } },
            });
            inviteChannelRef.current = remoteChannel;

            remoteChannel.subscribe((status) => {
              if (status === "SUBSCRIBED") {
                const sendInvite = async () => {
                  await remoteChannel.send({
                    type: "broadcast",
                    event: "call-signal",
                    payload: {
                      type: "call-invite",
                      conversationId: currentCall.conversationId,
                      callType: "voice",
                      from: user.id,
                      callerName: callerDisplayName,
                      callerAvatar: callerAvatarUrl,
                    },
                  });
                };
                sendInvite();
                inviteIntervalRef.current = setInterval(sendInvite, 2000);
              }
            });

            playDialTone();
          } catch {
            toast("Could not access microphone for call transition.");
          }
        }, 300);
        return;
      }
    }

    // Normal call end flow
    // If caller ends before connected, send explicit cancel; otherwise just clean up
    if (!currentCall?.isIncoming && durationSeconds === 0) {
      cancelOutgoingCall();
    } else {
      stopInviteSignaling();
    }

    if (currentCall && user) {
      const callStatus = durationSeconds > 0 ? "completed" : "no_answer";
      await logCallHistory({
        callerId: currentCall.isIncoming ? currentCall.remoteUserId : user.id,
        receiverId: currentCall.isIncoming ? user.id : currentCall.remoteUserId,
        conversationId: currentCall.conversationId,
        callType: currentCall.callType,
        status: callStatus,
        durationSeconds,
        startedAt: new Date(Date.now() - durationSeconds * 1000).toISOString(),
        endedAt: new Date().toISOString(),
      });

      if (durationSeconds > 0) {
        const mins = Math.floor(durationSeconds / 60);
        const secs = durationSeconds % 60;
        const durStr = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
        const label = currentCall.callType === "video" ? "Video call" : "Voice call";
        supabase.from("messages").insert({
          conversation_id: currentCall.conversationId,
          sender_id: user.id,
          content: `📞 ${label} — ${durStr}`,
          message_type: "text",
        });
      }
    }

    // Track stopping is handled by CallOverlay.cleanup() — no need to double-stop here.
    setActiveCall(null);
    incomingFromRef.current = null;
  }, [activeCall, user, stopInviteSignaling, cancelOutgoingCall]);

  return (
    <CallContext.Provider value={{ startCall, stopInviteSignaling, cancelOutgoingCall, restartInviteSignaling, activeCall, incomingCall, dnd, setDnd, minimized, setMinimized }}>
      {children}

      {/* Global call overlays — rendered on any page */}
      <Suspense fallback={null}>
        {activeCall && (
          <CallOverlay
            conversationId={activeCall.conversationId}
            callType={activeCall.callType}
            isIncoming={activeCall.isIncoming}
            myUserId={user!.id}
            remoteUserId={activeCall.remoteUserId}
            remoteName={activeCall.remoteName}
            remoteAvatar={activeCall.remoteAvatar}
            localStream={activeCall.localStream}
            onEnd={handleCallEnd}
            isAiCall={activeCall.isAiCall}
            minimized={minimized}
            onToggleMinimize={() => setMinimized(!minimized)}
          />
        )}

        {incomingCall && !activeCall && (
          <IncomingCallSheet
            callerName={incomingCall.callerName}
            callerAvatar={incomingCall.callerAvatar}
            callType={incomingCall.callType}
            onAccept={acceptCall}
            onDecline={declineCall}
          />
        )}
      </Suspense>
    </CallContext.Provider>
  );
}

// Helper — stopDialTone is just stopRingtone (same mechanism)
function stopDialTone() {
  stopRingtone();
}
